import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API });

client.interceptors.request.use((config) => {
  const token = localStorage.getItem('fier_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let onUnauthorized = null;
export function setUnauthorizedHandler(fn) { onUnauthorized = fn; }

client.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401 && onUnauthorized) onUnauthorized();
    return Promise.reject(err);
  }
);

export function apiError(err, fallback = 'Si è verificato un errore') {
  return err?.response?.data?.detail || err?.message || fallback;
}

// ---- Auth ----
export const login = (email, password) => client.post('/auth/login', { email, password }).then(r => r.data);
export const getMe = () => client.get('/auth/me').then(r => r.data);
export const changePassword = (old_password, new_password) => client.post('/auth/change-password', { old_password, new_password }).then(r => r.data);

// ---- Scenarios ----
export const getScenarios = () => client.get('/scenarios').then(r => r.data);
export const getActiveScenario = () => client.get('/scenarios/active').then(r => r.data);
export const getScenario = (id) => client.get(`/scenarios/${id}`).then(r => r.data);
export const updateScenario = (id, payload) => client.patch(`/scenarios/${id}`, payload).then(r => r.data);
export const createScenario = (payload) => client.post('/scenarios', payload).then(r => r.data);
export const activateScenario = (id) => client.post(`/scenarios/${id}/activate`).then(r => r.data);
export const deleteScenario = (id) => client.delete(`/scenarios/${id}`).then(r => r.data);
export const getKpi = (id) => client.get(`/scenarios/${id}/kpi`).then(r => r.data);
export const getScenarioReportPdf = (id) => client.get(`/scenarios/${id}/report/pdf`, { responseType: 'blob' }).then(r => r.data);
export const getScenarioReteVenditaPdf = (id) => client.get(`/scenarios/${id}/report/rete-vendita/pdf`, { responseType: 'blob' }).then(r => r.data);
export const getScenarioCategoryPdf = (id, listName) => client.get(`/scenarios/${id}/report/category/${listName}/pdf`, { responseType: 'blob' }).then(r => r.data);
export const getMonthly = (id) => client.get(`/scenarios/${id}/monthly`).then(r => r.data);

// ---- Items (revenues/fixed_costs/variable_costs/marketing_items/sponsor_items/depreciation_items) ----
export const addItem = (sid, list, payload) => client.post(`/scenarios/${sid}/items/${list}`, payload).then(r => r.data);
export const updateItem = (sid, list, itemId, payload) => client.patch(`/scenarios/${sid}/items/${list}/${itemId}`, payload).then(r => r.data);
export const deleteItem = (sid, list, itemId) => client.delete(`/scenarios/${sid}/items/${list}/${itemId}`).then(r => r.data);

// ---- Mix (product_mix / client_mix) ----
export const addMix = (sid, list, payload) => client.post(`/scenarios/${sid}/mix/${list}`, payload).then(r => r.data);
export const updateMix = (sid, list, itemId, payload) => client.patch(`/scenarios/${sid}/mix/${list}/${itemId}`, payload).then(r => r.data);
export const deleteMix = (sid, list, itemId) => client.delete(`/scenarios/${sid}/mix/${list}/${itemId}`).then(r => r.data);

// ---- Agents ----
export const addAgent = (sid, payload) => client.post(`/scenarios/${sid}/agents`, payload).then(r => r.data);
export const updateAgent = (sid, id, payload) => client.patch(`/scenarios/${sid}/agents/${id}`, payload).then(r => r.data);
export const deleteAgent = (sid, id) => client.delete(`/scenarios/${sid}/agents/${id}`).then(r => r.data);

// ---- Employees ----
export const addEmployee = (sid, payload) => client.post(`/scenarios/${sid}/employees`, payload).then(r => r.data);
export const updateEmployee = (sid, id, payload) => client.patch(`/scenarios/${sid}/employees/${id}`, payload).then(r => r.data);
export const deleteEmployee = (sid, id) => client.delete(`/scenarios/${sid}/employees/${id}`).then(r => r.data);

// ---- China imports ----
export const addChinaImport = (sid, payload) => client.post(`/scenarios/${sid}/china-imports`, payload).then(r => r.data);
export const updateChinaImport = (sid, id, payload) => client.patch(`/scenarios/${sid}/china-imports/${id}`, payload).then(r => r.data);
export const deleteChinaImport = (sid, id) => client.delete(`/scenarios/${sid}/china-imports/${id}`).then(r => r.data);

// ---- Controllo di Gestione ----
export const cgList = () => client.get('/cg/controls').then(r => r.data);
export const cgCreate = (payload) => client.post('/cg/controls', payload).then(r => r.data);
export const cgPatch = (id, payload) => client.patch(`/cg/controls/${id}`, payload).then(r => r.data);
export const cgGet = (id) => client.get(`/cg/controls/${id}`).then(r => r.data);
export const cgDelete = (id) => client.delete(`/cg/controls/${id}`).then(r => r.data);
export const cgUpload = (id, payload) => client.post(`/cg/controls/${id}/upload`, payload).then(r => r.data);
export const cgAnalysis = (id) => client.get(`/cg/controls/${id}/analysis`).then(r => r.data);
export const cgAsk = (id, question) => client.post(`/cg/controls/${id}/ai`, { question }).then(r => r.data);

// ---- Bilanci al 31/12 (dati solidi) ----
export const cgYearends = () => client.get('/cg/yearends').then(r => r.data);
export const cgYearendUpsert = (payload) => client.post('/cg/yearends', payload).then(r => r.data);
export const cgYearendDelete = (id) => client.delete(`/cg/yearends/${id}`).then(r => r.data);

// ---- Backup ----
export const backupsList = () => client.get('/backups').then(r => r.data);
export const backupCreate = (note) => client.post('/backups', { note }).then(r => r.data);
export const backupDownload = (id) => client.get(`/backups/${id}/download`).then(r => r.data);
export const backupRestore = (id) => client.post(`/backups/${id}/restore`).then(r => r.data);
export const backupDelete = (id) => client.delete(`/backups/${id}`).then(r => r.data);

// ---- Utenti (admin) ----
export const usersList = () => client.get('/users').then(r => r.data);
export const userCreate = (payload) => client.post('/users', payload).then(r => r.data);
export const userUpdate = (email, payload) => client.patch(`/users/${encodeURIComponent(email)}`, payload).then(r => r.data);
export const userResetPassword = (email, new_password) => client.post(`/users/${encodeURIComponent(email)}/reset-password`, { new_password }).then(r => r.data);
export const userDelete = (email) => client.delete(`/users/${encodeURIComponent(email)}`).then(r => r.data);

// ---- Registro attività (audit) ----
export const auditList = (params = {}) => client.get('/audit', { params }).then(r => r.data);

// ============================================================
// ANALISI DI CONTROLLO DI GESTIONE (modulo avanzato multi-società)
// ============================================================
export const cgaMeta = () => client.get('/cga/meta').then(r => r.data);
// Società
export const cgaCompanies = () => client.get('/cga/companies').then(r => r.data);
export const cgaCompanyCreate = (p) => client.post('/cga/companies', p).then(r => r.data);
export const cgaCompanyUpdate = (id, p) => client.patch(`/cga/companies/${id}`, p).then(r => r.data);
export const cgaCompanyDelete = (id) => client.delete(`/cga/companies/${id}`).then(r => r.data);
// Piano dei conti
export const cgaAccounts = (params = {}) => client.get('/cga/accounts', { params }).then(r => r.data);
export const cgaAccountCreate = (p) => client.post('/cga/accounts', p).then(r => r.data);
export const cgaAccountUpdate = (id, p) => client.patch(`/cga/accounts/${id}`, p).then(r => r.data);
export const cgaAccountDelete = (id) => client.delete(`/cga/accounts/${id}`).then(r => r.data);
// File / mastrini
export const cgaFiles = (params = {}) => client.get('/cga/files', { params }).then(r => r.data);
export const cgaFileGet = (id) => client.get(`/cga/files/${id}`).then(r => r.data);
export const cgaFileUpload = (p) => client.post('/cga/files', p).then(r => r.data);
export const cgaFileDelete = (id) => client.delete(`/cga/files/${id}`).then(r => r.data);
export const cgaLedger = (params = {}) => client.get('/cga/ledger', { params }).then(r => r.data);
// Schemi & regole
export const cgaSchemas = () => client.get('/cga/schemas').then(r => r.data);
export const cgaSchemaCreateDefault = () => client.post('/cga/schemas/default').then(r => r.data);
export const cgaSchemaCreate = (p) => client.post('/cga/schemas', p).then(r => r.data);
export const cgaSchemaUpdate = (id, p) => client.patch(`/cga/schemas/${id}`, p).then(r => r.data);
export const cgaSchemaDelete = (id) => client.delete(`/cga/schemas/${id}`).then(r => r.data);
export const cgaRules = (schema_id) => client.get('/cga/rules', { params: { schema_id } }).then(r => r.data);
export const cgaRuleCreate = (p) => client.post('/cga/rules', p).then(r => r.data);
export const cgaRuleUpdate = (id, p) => client.patch(`/cga/rules/${id}`, p).then(r => r.data);
export const cgaRuleDelete = (id) => client.delete(`/cga/rules/${id}`).then(r => r.data);
export const cgaDryRun = (p) => client.post('/cga/rules/dry-run', p).then(r => r.data);
export const cgaAutoGenerateRules = (p) => client.post('/cga/rules/auto-generate', p).then(r => r.data);
// Intercompany
export const cgaIntercompany = () => client.get('/cga/intercompany').then(r => r.data);
export const cgaIcCreate = (p) => client.post('/cga/intercompany', p).then(r => r.data);
export const cgaIcUpdate = (id, p) => client.patch(`/cga/intercompany/${id}`, p).then(r => r.data);
export const cgaIcDelete = (id) => client.delete(`/cga/intercompany/${id}`).then(r => r.data);
export const cgaConsolidate = (p) => client.post('/cga/consolidate', p).then(r => r.data);
// Analisi & versioni
export const cgaAnalyses = () => client.get('/cga/analyses').then(r => r.data);
export const cgaAnalysisGet = (id) => client.get(`/cga/analyses/${id}`).then(r => r.data);
export const cgaAnalysisCreate = (p) => client.post('/cga/analyses', p).then(r => r.data);
export const cgaAnalysisUpdate = (id, p) => client.patch(`/cga/analyses/${id}`, p).then(r => r.data);
export const cgaAnalysisDelete = (id) => client.delete(`/cga/analyses/${id}`).then(r => r.data);
export const cgaAnalysisCompute = (id) => client.post(`/cga/analyses/${id}/compute`).then(r => r.data);
export const cgaAnalysisVersions = (id) => client.get(`/cga/analyses/${id}/versions`).then(r => r.data);
export const cgaAnalysisDelta = (id) => client.get(`/cga/analyses/${id}/delta`).then(r => r.data);
export const cgaAdjustmentsList = (aid) => client.get(`/cga/analyses/${aid}/adjustments`).then(r => r.data);
export const cgaAdjustmentCreate = (aid, p) => client.post(`/cga/analyses/${aid}/adjustments`, p).then(r => r.data);
export const cgaAdjustmentDelete = (adjId) => client.delete(`/cga/adjustments/${adjId}`).then(r => r.data);
export const cgaAnalysisAI = (id, question) => client.post(`/cga/analyses/${id}/ai`, { question }).then(r => r.data);
export const cgaAnalysisExport = (id, format) => client.get(`/cga/analyses/${id}/export`, { params: { format }, responseType: 'blob' }).then(r => r.data);
export const cgaAnalysisReport = (id) => client.get(`/cga/analyses/${id}/report`, { responseType: 'blob' }).then(r => r.data);

// Partitari (drill-down + elisioni intercompany)
export const cgaPartitariAccounts = (params = {}) => client.get('/cga/partitari/accounts', { params }).then(r => r.data);
export const cgaPartitariEntries = (params = {}) => client.get('/cga/partitari/entries', { params }).then(r => r.data);
export const cgaPartitariIcSummary = (year = 2025) => client.get('/cga/partitari/intercompany-summary', { params: { year } }).then(r => r.data);
export const cgaPartitariIngest = (p) => client.post('/cga/partitari/ingest', p).then(r => r.data);
export const cgaPartitariApplyEliminations = (year = 2025) => client.post('/cga/partitari/apply-eliminations', null, { params: { year } }).then(r => r.data);

// Elisioni patrimoniali (crediti/debiti infragruppo)
export const cgaPatrimonialeSummary = (year = 2025) => client.get('/cga/patrimoniale/intercompany-summary', { params: { year } }).then(r => r.data);
export const cgaPatrimonialeApply = (year = 2025) => client.post('/cga/patrimoniale/apply-eliminations', null, { params: { year } }).then(r => r.data);

// Bilanci Fier S.p.A.
export const getFierBilanci = () => client.get('/fier/bilanci').then(r => r.data);
export const getFierBilancio = (year) => client.get(`/fier/bilanci/${year}`).then(r => r.data);
export const uploadFierBilancio = (formData, year) => client.post(`/fier/bilanci/upload${year ? `?year=${year}` : ''}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
export const deleteFierBilancio = (year) => client.delete(`/fier/bilanci/${year}`).then(r => r.data);
export const fierChat = (payload) => client.post('/fier/chat', payload).then(r => r.data);
export const getFierPartitario = (year) => client.get(`/fier/partitari/${year}`).then(r => r.data);
export const getFierMovimenti = (year, conto) => client.get(`/fier/partitari/${year}/movimenti`, { params: { conto } }).then(r => r.data);
export const getFierQuadratura = (year) => client.get(`/fier/quadratura/${year}`).then(r => r.data);
export const uploadFierPartitario = (formData, year) => client.post(`/fier/partitari/upload${year ? `?year=${year}` : ''}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);

// Bilanci riclassificati di periodo (Fier S.p.A.)
export const getFierPeriodi = () => client.get('/fier/periodi').then(r => r.data);
export const getFierPeriodo = (pid) => client.get(`/fier/periodi/${pid}`).then(r => r.data);
export const uploadFierPeriodo = (formData, opts = {}) => {
  const qs = new URLSearchParams();
  if (opts.label) qs.set('label', opts.label);
  if (opts.period_end) qs.set('period_end', opts.period_end);
  const q = qs.toString();
  return client.post(`/fier/periodi/upload${q ? `?${q}` : ''}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
};
export const uploadFierPreview = (formData, tipo) => client.post(`/fier/upload/preview?tipo=${tipo}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
export const deleteFierPeriodo = (pid) => client.delete(`/fier/periodi/${pid}`).then(r => r.data);
export const getFierPeriodoMovimenti = (pid, conto) => client.get(`/fier/periodi/${pid}/movimenti`, { params: { conto } }).then(r => r.data);
export const classificaFierPeriodo = (pid, assegnazioni) => client.post(`/fier/periodi/${pid}/classifica`, { assegnazioni }).then(r => r.data);
export const rettificaFierPeriodo = (pid, payload) => client.post(`/fier/periodi/${pid}/rettifiche`, payload).then(r => r.data);
export const fierPeriodoChat = (pid, payload) => client.post(`/fier/periodi/${pid}/chat`, payload).then(r => r.data);

// Piano dei Conti consolidato (Fier S.p.A.)
export const getFierPianoConti = (company) => client.get('/fier/piano-conti', { params: company ? { company } : {} }).then(r => r.data);
export const classificaFierPianoConti = (company, assegnazioni) => client.post('/fier/piano-conti/classifica', { company, assegnazioni }).then(r => r.data);
export const fierPianoContiChat = (payload) => client.post('/fier/piano-conti/chat', payload).then(r => r.data);
export const suggerisciFierPianoConti = (company) => client.post('/fier/piano-conti/suggerisci', { company }).then(r => r.data);
export const getFierPianoContiLog = (company, limit = 200) => client.get('/fier/piano-conti/log', { params: { ...(company ? { company } : {}), limit } }).then(r => r.data);
export const getFierKpiConti = () => client.get('/fier/kpi-conti').then(r => r.data);

export default client;
